import React from 'react';
import { Wrench, RefreshCw } from 'lucide-react';
import useMaintenanceSchedule, {
  DEFAULT_MAINTENANCE_MESSAGE,
} from '../../hooks/useMaintenanceSchedule.js';

const MaintenanceBanner = ({ allowAdminBypass = false }) => {
  const { schedule, isMaintenanceActive } = useMaintenanceSchedule();

  // If maintenance is not actively blocking operations, banner renders nothing
  // (Scheduled countdown is displayed as a clean badge in UniversalTopBar before action buttons)
  if (!isMaintenanceActive) {
    return null;
  }

  const message = schedule?.message?.trim() || DEFAULT_MAINTENANCE_MESSAGE;

  // 2. Active Mode for Super Admin (Persistent Top Notice so Admin is never locked out)
  if (allowAdminBypass) {
    return (
      <aside
        role="alert"
        aria-live="assertive"
        className="relative z-40 w-full bg-gradient-to-r from-blue-700 via-blue-600 to-indigo-700 text-white border-b border-blue-800 shadow-sm"
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-5 py-2 sm:py-2.5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0 flex-1 text-xs sm:text-sm">
            <span className="relative flex h-2.5 w-2.5 flex-shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-sky-300 opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-white" />
            </span>
            <span className="font-semibold text-white flex items-center gap-1.5">
              <Wrench className="w-3.5 h-3.5 text-sky-200 inline" />
              Platform Maintenance Active:
            </span>
            <span className="truncate text-blue-100">
              All operator dashboards are currently showing the blurred maintenance screen.
            </span>
          </div>
          <div className="px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-white/20 border border-white/30 text-white flex-shrink-0">
            Active
          </div>
        </div>
      </aside>
    );
  }

  // 3. Active Mode for All Regular Dashboards (Full Frosted Blur Screen)
  return (
    <div
      role="alertdialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/30 backdrop-blur-xl transition-all duration-300 animate-fade-in"
    >
      <div className="relative w-full max-w-lg">
        {/* Ambient glow behind card */}
        <div className="absolute -inset-1.5 bg-gradient-to-r from-blue-600/25 via-indigo-600/20 to-sky-500/25 rounded-3xl blur-2xl opacity-75 pointer-events-none" />

        {/* Premium Frosted Glass Card */}
        <div className="relative bg-white/95 backdrop-blur-2xl rounded-3xl shadow-[0_25px_70px_rgba(15,23,42,0.25)] border border-white/80 p-7 sm:p-9 text-center overflow-hidden">
          {/* Top royal blue accent line */}
          <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-blue-600 via-indigo-600 to-sky-500" />

          {/* Layered Wrench Icon with Live Pulse Indicator */}
          <div className="relative mx-auto w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-b from-blue-50 to-blue-100/70 border border-blue-200/80 flex items-center justify-center shadow-md mb-5">
            <Wrench className="w-8 h-8 sm:w-10 sm:h-10 text-blue-600 transform -rotate-12 transition-transform duration-300" />

            <span className="absolute -top-1 -right-1 flex h-3.5 w-3.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-blue-600 border-2 border-white shadow-xs" />
            </span>
          </div>

          {/* Maintenance Status Badge */}
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200/80 mb-3 shadow-xs">
            <span className="w-2 h-2 rounded-full bg-blue-600 animate-pulse" />
            System Maintenance Mode Active
          </div>

          {/* Card Title */}
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 tracking-tight">
            System Maintenance In Progress
          </h2>

          {/* Framed User Content / Message Box */}
          <div className="bg-slate-50/90 rounded-2xl p-4 sm:p-5 border border-slate-200/60 shadow-xs mb-6 text-left sm:text-center">
            <p className="text-sm sm:text-base text-gray-700 leading-relaxed font-normal whitespace-pre-line">
              {message}
            </p>
          </div>

          {/* Footer Subtext with Slow Spin Refresh */}
          <div className="flex items-center justify-center gap-2 text-xs text-gray-500 font-medium">
            <RefreshCw
              className="w-3.5 h-3.5 text-blue-600 animate-spin flex-shrink-0"
              style={{ animationDuration: '4s' }}
            />
            <span>Operations will automatically resume once completed</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaintenanceBanner;
